// src/prototype/hooks/useExecuteHubState.ts
// ============================================================
// Client-side state for the Execute hub (Oct 21, v3), per use case: the approval workflow
// (per-step owner, due date, checklist, decision, reopen), notes on approval steps and
// implementation milestones, one activity log, the decision snapshot and the PMO handoff.
// Demo only: saved in this browser until the persisted decision -> outcome record is designed
// (Rahul). Key is in DEMO_STATE_KEYS, so the presenter reset clears it.
// ============================================================
import { useCallback, useEffect, useState } from 'react';
import type { Signoff, StepState } from '../data/execute/approvalWorkflow';

export type { Signoff, SignoffDecision } from '../data/execute/approvalWorkflow';

export const EXECUTE_HUB_KEY = 'dva_demo_execute_hub';
const CHANGE_EVENT = 'dva_demo_execute_hub_change';

export type NoteTarget =
  | { kind: 'approval'; step: number; label: string }
  | { kind: 'milestone'; title: string };

export interface Note { id: string; target: NoteTarget; text: string; author: string; at: string }

export type ActivityKind = 'note' | 'check' | 'uncheck' | 'decision' | 'reopen' | 'owner' | 'due' | 'milestone';
export interface ActivityEvent {
  id: string;
  at: string; // ISO timestamp
  actor: string;
  kind: ActivityKind;
  area: 'approval' | 'milestone';
  target: string;
  detail: string;
}

/** Snapshot captured at the moment of the final decision (decision half of decision -> outcome). */
export interface DecisionSnapshot {
  decision: Signoff['decision'];
  date: string;
  approvers: string[];
  compositeScore: number;
  verdict: string;
  scoringRun: string;
  scoresAsOf: string;
  annualValueRange: string;
  netValue3Yr: string;
  payback: string;
  keyAssumptions: string[];
}

export interface HandoffState {
  pmoOwner: string;
  handoffDate: string;
  status: 'Not started' | 'Handed off' | 'Accepted by PMO';
  roles: { 'Service-line and budget owner': string; 'Clinical champion': string; 'Application owner': string };
}

export interface UseCaseHubState {
  steps: Record<number, StepState>;
  notes: Note[];
  activity: ActivityEvent[];
  snapshot?: DecisionSnapshot;
  handoff: HandoffState;
}

export const EMPTY_HANDOFF: HandoffState = {
  pmoOwner: '',
  handoffDate: '',
  status: 'Not started',
  roles: { 'Service-line and budget owner': '', 'Clinical champion': '', 'Application owner': '' },
};

type Store = Record<string, Partial<UseCaseHubState> & { signoffs?: Record<number, Signoff> }>;

function readStore(): Store {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(EXECUTE_HUB_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function emptyState(): UseCaseHubState {
  return { steps: {}, notes: [], activity: [], handoff: { ...EMPTY_HANDOFF, roles: { ...EMPTY_HANDOFF.roles } } };
}

/** Normalize a stored entry; migrates the earlier `signoffs` shape into steps[i].decision. */
function normalize(entry: Store[string] | undefined): UseCaseHubState {
  const base = emptyState();
  if (!entry) return base;
  const steps: Record<number, StepState> = { ...(entry.steps ?? {}) };
  for (const [k, s] of Object.entries(entry.signoffs ?? {})) {
    const i = Number(k);
    steps[i] = { ...(steps[i] ?? {}), decision: steps[i]?.decision ?? s };
  }
  return {
    steps,
    notes: entry.notes ?? [],
    activity: entry.activity ?? [],
    snapshot: entry.snapshot,
    handoff: { ...base.handoff, ...(entry.handoff ?? {}), roles: { ...base.handoff.roles, ...(entry.handoff?.roles ?? {}) } },
  };
}

let seq = 0;
const newId = () => `${Date.now().toString(36)}-${(seq++).toString(36)}`;

export function useExecuteHubState(useCaseId: string) {
  const [state, setState] = useState<UseCaseHubState>(() => normalize(readStore()[useCaseId]));

  useEffect(() => {
    setState(normalize(readStore()[useCaseId]));
    const sync = () => setState(normalize(readStore()[useCaseId]));
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('ea_demo_reset', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('ea_demo_reset', sync);
    };
  }, [useCaseId]);

  const update = useCallback((fn: (prev: UseCaseHubState) => UseCaseHubState) => {
    const store = readStore();
    const next = fn(normalize(store[useCaseId]));
    store[useCaseId] = next;
    try { localStorage.setItem(EXECUTE_HUB_KEY, JSON.stringify(store)); } catch { /* storage unavailable */ }
    setState(next);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  }, [useCaseId]);

  const log = (prev: UseCaseHubState, e: Omit<ActivityEvent, 'id' | 'at'>): ActivityEvent[] =>
    [{ id: newId(), at: new Date().toISOString(), ...e }, ...prev.activity];

  const patchStep = (prev: UseCaseHubState, i: number, patch: Partial<StepState>) =>
    ({ ...prev.steps, [i]: { ...(prev.steps[i] ?? {}), ...patch } });

  const setStepOwner = useCallback((i: number, label: string, ownerId: string, ownerName: string, actor: string) =>
    update((prev) => ({ ...prev, steps: patchStep(prev, i, { ownerId }), activity: log(prev, { actor, kind: 'owner', area: 'approval', target: label, detail: `Owner set to ${ownerName}` }) })), [update]);

  const setStepDue = useCallback((i: number, label: string, due: string, actor: string) =>
    update((prev) => ({ ...prev, steps: patchStep(prev, i, { due: due || undefined }), activity: log(prev, { actor, kind: 'due', area: 'approval', target: label, detail: due ? `Due date set to ${new Date(`${due}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Due date cleared' }) })), [update]);

  const toggleCheck = useCallback((i: number, label: string, item: string, done: boolean, actor: string) =>
    update((prev) => {
      const checks = { ...(prev.steps[i]?.checks ?? {}), [item]: { done, by: actor, at: new Date().toISOString() } };
      return { ...prev, steps: patchStep(prev, i, { checks }), activity: log(prev, { actor, kind: done ? 'check' : 'uncheck', area: 'approval', target: label, detail: `${done ? 'Completed' : 'Unchecked'}: ${item}` }) };
    }), [update]);

  const decide = useCallback((i: number, label: string, s: Signoff, actor: string) =>
    update((prev) => ({
      ...prev,
      steps: patchStep(prev, i, { decision: s }),
      activity: log(prev, { actor, kind: 'decision', area: 'approval', target: label, detail: `${s.decision[0].toUpperCase()}${s.decision.slice(1)}: ${s.rationale}${s.reversal ? ` (would reverse if: ${s.reversal})` : ''}` }),
    })), [update]);

  const reopen = useCallback((i: number, label: string, reason: string, prior: string, actor: string) =>
    update((prev) => ({
      ...prev,
      steps: patchStep(prev, i, { decision: undefined, reopened: true }),
      snapshot: undefined,
      activity: log(prev, { actor, kind: 'reopen', area: 'approval', target: label, detail: `Reopened (was ${prior}): ${reason}` }),
    })), [update]);

  const addNote = useCallback((target: NoteTarget, text: string, actor: string) =>
    update((prev) => ({
      ...prev,
      notes: [{ id: newId(), target, text, author: actor, at: new Date().toISOString() }, ...prev.notes],
      activity: log(prev, { actor, kind: 'note', area: target.kind, target: target.kind === 'approval' ? target.label : target.title, detail: text }),
    })), [update]);

  const logMilestone = useCallback((title: string, detail: string, actor: string) =>
    update((prev) => ({ ...prev, activity: log(prev, { actor, kind: 'milestone', area: 'milestone', target: title, detail }) })), [update]);

  const setSnapshot = useCallback((snapshot: DecisionSnapshot) =>
    update((prev) => ({ ...prev, snapshot })), [update]);

  const clearApproval = useCallback(() =>
    update((prev) => ({ ...prev, steps: {}, notes: prev.notes.filter((n) => n.target.kind !== 'approval'), activity: prev.activity.filter((a) => a.area !== 'approval'), snapshot: undefined })), [update]);

  const setHandoff = useCallback((patch: Partial<HandoffState>) =>
    update((prev) => ({ ...prev, handoff: { ...prev.handoff, ...patch } })), [update]);

  return { state, setStepOwner, setStepDue, toggleCheck, decide, reopen, addNote, logMilestone, setSnapshot, clearApproval, setHandoff };
}

export default useExecuteHubState;
